import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import {
  getConversionFactorValue,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { getProjectGfa } from '@shared/helpers/project.helpers';
import { getCPhaseBuildingResults } from '@shared/helpers/results/c-phase/c-phase.helpers';
import { getOperationalWaterResults } from '@shared/helpers/water-use.helpers';
import type { Project } from '@shared/models/project.model';
import type {
  Results,
  SbefGroupResultsRecord,
} from '@shared/models/unit.model';

/**
 * SBEF scope group `'7'` = BD7 (interior surface layers), `'8'` = BD8 (installations).
 * Used to decide whether actual modelled resources exist for those building parts so
 * the C3-C4 schablon is only applied when they are missing (IVL, DEV-3327).
 */
const BD7_SBEF_GROUP_KEY: SbefScopeGroupKey =
  SbefGroupCode.InteriorSurfaceLayersRoomCompletion;
const BD8_SBEF_GROUP_KEY: SbefScopeGroupKey = SbefGroupCode.Installations;

/**
 * Whether a version SBEF bucket holds actual modelled resources (any mass or A-stage
 * emissions), used to gate the C3-C4 BD7/BD8 schablon.
 */
const hasSbefGroupResources = (
  buckets: SbefGroupResultsRecord,
  key: SbefScopeGroupKey,
): boolean => {
  const bucket = buckets[key];
  if (!bucket) return false;
  return (
    (bucket.kg ?? 0) > 0 || getConversionFactorValue(bucket, 'co2e_A') !== 0
  );
};

/**
 * Results that belong to the building as a whole rather than to any single element, and
 * are therefore added once on the version row instead of rolling up from element rows:
 * - B7 operational water use (occupants × schablon)
 * - C1 demolition base energy (GFA-based electricity + diesel)
 * - C3-C4 BD7/BD8 schablon, applied only for the building parts (SBEF groups 7/8) that
 *   have no actual modelled resources
 *
 * Identical for the baseline and every proposal of a version except for the BD7/BD8
 * gating, which follows the resources present in that scenario.
 *
 * @param project - Enriched project; supplies occupants and GFA.
 * @param versionBuckets - Version-level SBEF breakdown gating the BD7/BD8 schablon.
 */
export const getBuildingLevelResults = (
  project: Project,
  versionBuckets: SbefGroupResultsRecord,
): Results =>
  sumConversionFactors(
    getOperationalWaterResults(project),
    getCPhaseBuildingResults(project, getProjectGfa(project), {
      includeBd7: !hasSbefGroupResources(versionBuckets, BD7_SBEF_GROUP_KEY),
      includeBd8: !hasSbefGroupResources(versionBuckets, BD8_SBEF_GROUP_KEY),
    }),
  );
